import { Empty, Tag, Tooltip } from 'antd';
import { EyeInvisibleOutlined, FileTextOutlined } from '@ant-design/icons';
import type { EvidenceView } from '@/api/types';
import { formatDay } from '@/lib/time';

/**
 * Evidence rows with content address + provenance (F-IS-02). The evidence read model exposes a
 * content address and PII-scrubbed metadata only — never a storage payload — and carries no
 * separate valid_from/valid_to window: {@code capturedAt} (source-asserted) and {@code pinnedAt}
 * (when this hub linked it) are the two honest provenance bounds. {@code atMs} is retained as the
 * point-in-time read coordinate for future window rendering; today it is unused (nothing is
 * synthesised, per the backend evidence surface).
 */
export function EvidenceList({
  evidence,
  compact,
}: {
  evidence: EvidenceView[];
  atMs?: number;
  compact?: boolean;
}) {
  if (!evidence || evidence.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No evidence records." />;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {evidence.map((e, i) => {
        const bits = [e.source, e.mediaType].filter(Boolean).join(' · ');
        return (
          <div className="ol-card" key={e.contentHash || i} style={{ padding: compact ? '9px 11px' : '12px 14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
              <span style={{ fontWeight: 600, fontSize: 12.5, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <FileTextOutlined aria-hidden />
                {e.mediaType ?? 'Evidence'}
              </span>
              {e.redacted && (
                <Tooltip title="This evidence has been redacted (erasure shell).">
                  <Tag bordered={false} color="warning">
                    <EyeInvisibleOutlined aria-hidden /> Redacted
                  </Tag>
                </Tooltip>
              )}
            </div>
            {bits && (
              <div style={{ fontSize: 11.5, color: 'var(--ol-text-secondary)', marginTop: 5 }}>
                {bits}
                {e.capturedAt ? ` · captured ${formatDay(e.capturedAt)}` : ''}
                {e.pinnedAt ? ` · pinned ${formatDay(e.pinnedAt)}` : ''}
              </div>
            )}
            {e.contentHash && (
              <div
                className="ol-mono"
                style={{ fontSize: 10.5, color: 'var(--ol-text-tertiary)', marginTop: 4, wordBreak: 'break-all' }}
                title={e.contentHash}
              >
                {e.contentHash}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
